import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

/*
 * VisitOne button. One primary per page or overlay; destructive is a distinct
 * variant and never the primary action. Labels are verb + object. A label
 * wraps rather than overflowing when the button is narrower than it (WCAG
 * 1.4.10), so sizes set a minimum height.
 */
const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-sm text-center text-sm font-medium transition-colors select-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5",
  {
    variants: {
      variant: {
        primary:
          'bg-action text-action-fg hover:bg-action-hover active:bg-action-active',
        secondary:
          'border border-line bg-surface text-fg hover:bg-surface-hover',
        ghost: 'text-fg hover:bg-surface-hover',
        destructive:
          'border border-danger/60 bg-danger-surface text-danger hover:border-danger',
        link: 'text-action underline-offset-4 hover:underline',
      },
      size: {
        sm: 'min-h-8 px-2.5 py-1 text-xs',
        default: 'min-h-9 px-3.5 py-1.5',
        lg: 'min-h-11 px-5 py-2',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'secondary',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

function Button({ className, variant, size, type = 'button', ...props }: ButtonProps) {
  return (
    <button
      type={type}
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
